import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiService from '../api';
import '../styles/pages/ProductDetail.scss';

// Imagens para uso offline (fallback)
import pijama1 from '../assets/images/pijama1.jpg';
import pijama2 from '../assets/images/pijama2.jpg';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [networkStatus, setNetworkStatus] = useState('checking');
  const [source, setSource] = useState('');

  // Lista de imagens de fallback para uso offline
  const fallbackImages = [pijama1, pijama2];

  // Produtos de demonstração para garantir que algo seja exibido
  const demoProducts = [
    {
      id: 1, 
      name: 'Pijama de Malha Fria',
      description: 'Pijama confortável de malha fria, ideal para noites quentes. Material leve e respirável.',
      price: 38.90,
      color: 'Azul',
      size: 'M',
      material: 'Malha Fria',
      images: [{ url: pijama1 }]
    },
    {
      id: 2, 
      name: 'Pijama de Algodão',
      description: 'Pijama macio de algodão premium, perfeito para todas as estações. Conforto garantido.',
      price: 45.90,
      color: 'Rosa',
      size: 'G',
      material: 'Algodão',
      images: [{ url: pijama2 }]
    }
  ];

  // Função para obter produtos do localStorage adicionados pelo admin
  const getProductFromLocalStorage = (productId) => {
    try {
      const storedProducts = localStorage.getItem('lunaProducts');
      if (storedProducts) {
        const parsedProducts = JSON.parse(storedProducts);
        // Convertemos o id para número se for string, pois o ID da URL sempre vem como string
        const numericId = Number(productId);
        const product = parsedProducts.find(p => p.id === numericId || p.id === productId);
        
        if (product) {
          console.log("Produto encontrado no localStorage:", product);
          return product;
        } else {
          console.log("Produto não encontrado no localStorage. IDs disponíveis:", 
            parsedProducts.map(p => p.id));
        }
      }
    } catch (error) {
      console.error("Erro ao buscar produto do localStorage:", error);
    }
    return null;
  };

  // Primeiro, verificamos a conexão com a API
  useEffect(() => {
    console.log("Verificando status da API...");
    const checkApiStatus = async () => {
      try {
        await apiService.testConnection();
        setNetworkStatus('online');
        console.log("API está online");
      } catch (error) {
        console.error("API está offline:", error);
        setNetworkStatus('offline');
        
        // Se a API estiver offline, verificamos o localStorage
        const localProduct = getProductFromLocalStorage(id);
        if (localProduct) {
          console.log("Usando produto do localStorage");
          setProduct(localProduct);
          setSource('localStorage');
          setLoading(false);
        } else {
          // Se não houver o produto no localStorage, usamos o de demonstração
          const demoProduct = demoProducts.find(p => p.id === Number(id)) || demoProducts[0];
          console.log("Usando produto de demonstração");
          setProduct(demoProduct);
          setSource('demo');
          setLoading(false);
        }
      }
    };
    
    checkApiStatus();
  }, [id]);

  // Em seguida, buscamos o produto se a API estiver online
  useEffect(() => {
    if (networkStatus === 'online') {
      console.log("Buscando produto da API com ID:", id);
      
      const fetchProduct = async () => {
        try {
          const data = await apiService.getProductById(id);
          console.log("Produto recebido da API:", data);
          
          if (data) {
            setProduct(data);
            setSource('api');
            setLoading(false);
            setError(null);
          } else {
            console.warn("API retornou produto vazio");
            
            // Verificar se temos o produto no localStorage
            const localProduct = getProductFromLocalStorage(id);
            if (localProduct) {
              console.log("API sem produto, usando produto do localStorage");
              setProduct(localProduct);
              setSource('localStorage');
              setLoading(false);
            } else {
              // Se não houver o produto no localStorage, usamos o de demonstração
              const demoProduct = demoProducts.find(p => p.id === Number(id)) || demoProducts[0];
              setProduct(demoProduct);
              setSource('demo');
              setLoading(false);
            }
          }
        } catch (err) {
          console.error('Erro ao buscar produto:', err);
          
          // Verificar se temos o produto no localStorage
          const localProduct = getProductFromLocalStorage(id);
          if (localProduct) {
            console.log("Erro na API, usando produto do localStorage");
            setProduct(localProduct);
            setSource('localStorage');
            setLoading(false);
          } else {
            // Se não houver o produto no localStorage, usamos o de demonstração
            const demoProduct = demoProducts.find(p => p.id === Number(id)) || demoProducts[0];
            setProduct(demoProduct);
            setSource('demo');
            setLoading(false);
          }
        }
      };

      // Tentar buscar produto da API, com um tempo limite para fallback
      const timeoutId = setTimeout(() => {
        if (loading) {
          console.log("Timeout atingido. Verificando localStorage...");
          
          // Verificar se temos o produto no localStorage
          const localProduct = getProductFromLocalStorage(id);
          if (localProduct) {
            console.log("Timeout na API, usando produto do localStorage");
            setProduct(localProduct);
            setSource('localStorage');
            setLoading(false);
          } else {
            // Se não houver o produto no localStorage, usamos o de demonstração
            const demoProduct = demoProducts.find(p => p.id === Number(id)) || demoProducts[0];
            setProduct(demoProduct);
            setSource('demo');
            setLoading(false);
          }
        }
      }, 8000); // 8 segundos de timeout

      fetchProduct();

      return () => clearTimeout(timeoutId);
    }
  }, [networkStatus, id, loading]);

  const handleThumbnailClick = (index) => {
    setCurrentImage(index);
  };

  const handleNextImage = () => {
    if (product && product.images && product.images.length > 0) {
      setCurrentImage((prev) => (prev + 1) % product.images.length);
    }
  };

  const handlePrevImage = () => {
    if (product && product.images && product.images.length > 0) {
      setCurrentImage((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  const formatPrice = (price) => {
    return typeof price === 'number'
      ? `R$ ${price.toFixed(2).replace('.', ',')}`
      : 'Preço não disponível';
  };

  const getWhatsappLink = () => {
    const message = `Olá! Estou interessado no produto "${product.name}" (${formatPrice(product.price)}). Gostaria de mais informações.`;
    return `https://wa.me/5516992613356?text=${encodeURIComponent(message)}`;
  };

  // Debug render
  console.log("Renderizando - status da rede:", networkStatus, "loading:", loading, "error:", error, "produto:", product?.name, "fonte:", source);

  if (loading && networkStatus !== 'offline') {
    return (
      <motion.div 
        className="product-detail-page loading"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="container">
          <h2>Carregando produto...</h2>
          <p>Aguarde enquanto buscamos as informações</p>
        </div>
      </motion.div>
    );
  }

  if (!product) {
    return (
      <motion.div 
        className="product-detail-page error"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="container">
          <h2>Produto não encontrado</h2>
          <p>O produto que você está procurando não está disponível.</p>
          <Link to="/produtos" className="back-button">Voltar para Produtos</Link>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="product-detail-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="container">
        <div className="product-navigation">
          <Link to="/produtos" className="back-link">
            ← Voltar para Produtos
          </Link>
        </div>
        
        <div className="product-content">
          <div className="product-gallery">
            <div className="main-image">
              <button className="arrow prev" onClick={handlePrevImage}>
                &#10094;
              </button>
              <img 
                src={
                  product.images && product.images.length > 0 
                    ? (product.images[currentImage]?.url || product.images[currentImage]?.image_url) 
                    : fallbackImages[0]
                }
                alt={product.name}
                onError={(e) => {
                  console.log("Erro ao carregar imagem, usando fallback");
                  e.target.src = fallbackImages[0];
                }}
              />
              <button className="arrow next" onClick={handleNextImage}>
                &#10095;
              </button>
            </div>
            
            {product.images && product.images.length > 1 && (
              <div className="thumbnails">
                {product.images.map((image, index) => (
                  <div 
                    key={index} 
                    className={`thumbnail ${index === currentImage ? 'active' : ''}`}
                    onClick={() => handleThumbnailClick(index)}
                  >
                    <img 
                      src={image.url || image.image_url} 
                      alt={`${product.name} - imagem ${index + 1}`}
                      onError={(e) => {
                        console.log("Erro ao carregar thumbnail, usando fallback");
                        e.target.src = fallbackImages[index % fallbackImages.length];
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="product-info">
            <h1>{product.name}</h1>
            <p className="product-price">{formatPrice(product.price)}</p>
            
            <div className="product-description">
              <h3>Descrição</h3>
              <p>{product.description || "Descrição não disponível para este produto."}</p>
            </div>
            
            {(product.color || product.size || product.material) && (
              <div className="product-attributes">
                <h3>Especificações</h3>
                <ul>
                  {product.color && <li><strong>Cor:</strong> {product.color}</li>}
                  {product.size && <li><strong>Tamanho:</strong> {product.size}</li>}
                  {product.material && <li><strong>Material:</strong> {product.material}</li>}
                </ul>
              </div>
            )}
            
            <div className="product-actions">
              <a href={getWhatsappLink()} className="whatsapp-button" target="_blank" rel="noopener noreferrer">
                Comprar pelo WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductDetail; 